import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';

import { UserName } from '@sorare/core/src/components/user/UserName';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { isType } from '@sorare/core/src/lib/gql';

import {
  AccountEntry,
  messages,
} from '#src/components/mySorare/TransactionsHistory/SpentFiatPaymentIntents/AccountEntry/index.tsx';
import { TransactionsOffer } from '#src/components/mySorare/TransactionsHistory/common/TransactionsOffer/index.tsx';

import type { Offer_authorizationRequest } from './__generated__/index.graphql.ts';

type Props = {
  payment: Offer_authorizationRequest;
};

export const Offer = ({ payment }: Props) => {
  const { operation } = payment;
  const { currentUser } = useCurrentUserContext();
  const { formatMessage } = useIntl();

  if (!currentUser || !operation) return null;

  if (!isType(operation, 'TokenOffer') || !operation.sender) {
    return null;
  }

  if (
    (operation.receiver && !isType(operation.receiver, 'User')) ||
    !isType(operation.sender, 'User')
  ) {
    return null;
  }

  const data =
    operation.sender.slug === currentUser.slug
      ? {
          title: messages.offerTitleAsSender,
          otherParty: operation.receiver,
        }
      : {
          title: messages.offerTitleAsReceiver,
          otherParty: operation.sender,
        };

  return (
    <AccountEntry
      payment={payment}
      title={formatMessage(data.title, {
        otherParty: data.otherParty && <UserName user={data.otherParty} />,
      })}
    >
      <TransactionsOffer tokenOffer={operation} />
    </AccountEntry>
  );
};

Offer.fragments = {
  payment: gql`
    fragment Offer_authorizationRequest on AuthorizationRequest {
      id
      operation {
        ... on TokenOffer {
          id
          sender {
            ... on PublicUserInfoInterface {
              slug
              ...UserName_publicUserInfoInterface
            }
          }
          receiver {
            ... on PublicUserInfoInterface {
              slug
              ...UserName_publicUserInfoInterface
            }
          }
          ...TransactionsOffer_tokenOffer
        }
      }
      ...AccountEntry_authorizationRequest
    }
    ${TransactionsOffer.fragments.tokenOffer}
    ${AccountEntry.fragments.payment}
    ${UserName.fragments.user}
  ` as TypedDocumentNode<Offer_authorizationRequest>,
};
